import { saveAs } from "file-saver"
import { DateRange } from "react-day-picker"
import { utils, write } from "xlsx"
import { getOrderStatus } from "../components/tables/transactions-table/data/orders/ordersData"
import {
	TableMovement,
	TableOrder,
	TableView,
} from "../components/tables/transactions-table/data/types"

const formatDate = (dateString: string) => {
	const date = new Date(dateString)
	return date.toISOString().split("T")[0] // Returns YYYY-MM-DD
}

// Update formatNumber to handle negative values for outbound transactions
const formatNumber = (
	num: number | string | undefined,
	currency?: string
): string => {
	console.log(num)
	if (num === undefined) return '"0"'
	const numericValue =
		typeof num === "string" ? parseFloat(num.replace(",", "")) : num
	if (isNaN(numericValue)) return '"0"'

	// Set decimals based on currency
	const decimals = currency === "BNB" ? 8 : 2

	// Format number with comma decimal separator
	console.log(numericValue)
	const formattedNumber = Math.abs(numericValue).toLocaleString("es-AR", {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
		useGrouping: false,
	})

	console.log(formattedNumber)

	// Add the sign in front
	return `"${numericValue < 0 ? "-" : ""}${formattedNumber}"`
}

const formatNumberXLS = (
	num: number | string | undefined,
	currency?: string
): string => {
	console.log(num)
	if (num === undefined) return '"0"'
	const numericValue =
		typeof num === "string" ? parseFloat(num.replace(/,/g, "")) : num
	if (isNaN(numericValue)) return '"0"'

	// Set decimals based on currency
	const decimals = currency === "BNB" ? 8 : 2

	// Format number with comma decimal separator
	console.log(numericValue)
	const formattedNumber = Math.abs(numericValue).toLocaleString("es-AR", {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
		useGrouping: false,
	})

	console.log(formattedNumber)

	// Add the sign in front
	return `${numericValue < 0 ? "-" : ""}${formattedNumber}`
}

// Update escapeField to handle undefined values
const escapeField = (field: string | number | undefined): string => {
	if (field === undefined) return '""'
	if (typeof field === "number") return formatNumber(field)
	return `"${field.replace(/"/g, '""')}"`
}

type TransactionState =
	| "SUCCESS"
	| "PENDING"
	| "UNDERPAID"
	| "OVERPAID"
	| "EXPIRED"

type MovementState = {
	INBOUND: {
		PROCESSED: "RECIBIDO"
		UNMATCHED: "SIN ASIGNAR"
	}
	OUTBOUND: {
		PROCESSED: "ENVIADO"
		PENDING: "PENDIENTE"
		REJECTED: "RECHAZADO"
	}
}

const TRANSACTION_STATUS_MAP: Record<TransactionState, string> = {
	SUCCESS: "COMPLETO",
	PENDING: "PENDIENTE Pago",
	UNDERPAID: "INSUFICIENTE",
	OVERPAID: "SOBRANTE",
	EXPIRED: "EXPIRADO",
}

const MOVEMENT_STATUS_MAP: MovementState = {
	INBOUND: {
		PROCESSED: "RECIBIDO",
		UNMATCHED: "SIN ASIGNAR",
	},
	OUTBOUND: {
		PROCESSED: "ENVIADO",
		PENDING: "PENDIENTE",
		REJECTED: "RECHAZADO",
	},
}

export const translateTransactionStatus = (
	status: string,
	type?: "INBOUND" | "OUTBOUND"
): string => {
	if (type) {
		const movementStatus =
			MOVEMENT_STATUS_MAP[type][
				status as keyof (typeof MOVEMENT_STATUS_MAP)[typeof type]
			]
		return movementStatus || status
	}
	return TRANSACTION_STATUS_MAP[status as TransactionState] || status
}

// Add this helper function to normalize dates for CSV export
const normalizeDate = (dateStr: string): string => {
	const today = new Date()
	const yesterday = new Date(today)
	yesterday.setDate(yesterday.getDate() - 1)

	// Convert 'Hoy' to current date
	if (dateStr.toLowerCase() === "hoy") {
		return today.toLocaleDateString("es-AR", {
			day: "2-digit",
			month: "2-digit",
			year: "2-digit",
		})
	}

	// Convert 'Ayer' to yesterday's date
	if (dateStr.toLowerCase() === "ayer") {
		return yesterday.toLocaleDateString("es-AR", {
			day: "2-digit",
			month: "2-digit",
			year: "2-digit",
		})
	}

	// Return original date if it's already in DD/MM/YY format
	return dateStr
}

const generateXLS = (data: TableOrder[] | TableMovement[], type: TableView) => {
	if (type === "movements") {
		return (data as TableMovement[]).map((item) => {
			if (item.type === "INBOUND") {
				return {
					"Número de Orden":
						item.status === "UNMATCHED" ? "SIN ASIGNAR" : item.orderId || "-",
					Enviado: 0,
					Recibido: formatNumberXLS(item.amountReceived, item.currency),
					Comisión: formatNumberXLS(
						item.movement_type === "transfer"
							? item.commission.commission.amount
							: item.amountCommission,
						item.currency
					),
					Impuestos:
						item.movement_type === "transfer" && item.taxes
							? Number(item.taxes?.total_tax_amount ?? 0) +
							  Number(item.commission.commission_tax_total_amount ?? 0)
							: 0,
					Acreditado: formatNumberXLS(item.amountCredited, item.currency),
					Moneda: item.currency,
					Estado:
						item.status === "UNMATCHED"
							? "RECIBIDO"
							: translateTransactionStatus(item.status, "INBOUND"),
					"Fecha Movimiento": normalizeDate(item.time.formatted.date),
					"Hora Movimiento": item.time.formatted.time,
					Titular:
						item.sender && item.sender.type === "fiat"
							? item.sender.titular
							: item.sender.address || "-",
					...(item.movement_type === "transfer" &&
						item.customer_id && { "ID Cliente": item.customer_id }),
				}
			}
			if (item.type === "OUTBOUND") {
				return {
					"Número de Orden": "-",
					Enviado: formatNumberXLS(item.amountSent, item.currency),
					Impuestos: 0,
					Recibido: 0,
					Comisión: 0,
					Acreditado: 0,
					Moneda: item.currency,
					Estado: translateTransactionStatus(item.status, "OUTBOUND"),
					"Fecha Movimiento": normalizeDate(item.time.formatted.date),
					"Hora Movimiento": item.time.formatted.time,
					Titular:
						item.recipient && item.recipient.type === "fiat"
							? item.recipient.titular
							: item.recipient.address || "-",
				}
			}
		})
	} else {
		return (data as TableOrder[]).map((item) => {
			const currency =
				(item.currency !== "-" && item.currency) ||
				(item.expected?.currency !== "-" && item.expected?.currency) ||
				(item.received?.currency !== "-" && item.received?.currency) ||
				"Unknown"

			// Don't show payment date for pending or expired status
			const showPaymentDate = !["PENDING", "EXPIRED"].includes(
				item.status.toUpperCase()
			)

			return {
				"Número de Orden": item.store?.display_id,
				Recibido: formatNumberXLS(
					item.received?.amount || item.expected?.amount || 0,
					currency
				),
				Acreditado: formatNumberXLS(
					item.credited?.amount || item.received?.amount || 0,
					currency
				),
				Comisión: formatNumberXLS(
					item.commission?.commission?.amount || 0,
					currency
				),
				Impuestos:
					item.currencyType === "crypto"
						? 0
						: Number(item.taxes?.total_tax_amount ?? 0) +
						  Number(item.commission?.commission_tax_total_amount ?? 0),
				Moneda: currency,
				"CVU/CBU Comprador":
					item.sender.type === "fiat"
						? item.sender.cvu
						: item.sender.address ?? "-",
				Estado: translateTransactionStatus(item.status).toUpperCase(),
				"Cambio Manual":
					getOrderStatus(item)?.type === "confirm"
						? "CONFIRMADO"
						: getOrderStatus(item)?.type === "cancel"
						? "CANCELADO"
						: "-",
				"Fecha Orden": item.time.formatted.date,
				"Hora Orden": item.creationDateTime?.formatted.time,
				"Fecha Pago": showPaymentDate
					? item.lastModifiedDateTime?.formatted.date
					: "-",
				"Hora Pago": showPaymentDate
					? item.lastModifiedDateTime?.formatted.time
					: "-",
			}
		})
	}
}

// // Update the generateCSV function to include both dates
// const generateCSV = (transactions: TableOrder[]) => {
// 	const headers = [
// 		"Número de Orden",
// 		"Id de orden",
// 		"Monto",
// 		"Moneda",
// 		"CVU/CBU Comprador",
// 		"Estado",
// 		"Fecha Orden",
// 		"Hora Orden",
// 		"Fecha Pago",
// 		"Hora Pago",
// 		"Tipo",
// 	]

// 	const csvContent = [headers.map(escapeField).join(",")]

// 	if (transactions && transactions.length > 0) {
// 		const filteredTransactions = transactions.filter(
// 			(tx) =>
// 				tx.status !== "EXPIRED" &&
// 				tx.status !== "PENDING" &&
// 				tx.status !== "pending"
// 		)

// 		filteredTransactions
// 			.sort((a, b) => {
// 				return a.time.time.getTime() - b.time.time.getTime()
// 			})
// 			.forEach((tx) => {
// 				if (tx.currency === "USDT") {
// 					console.log(tx)
// 				}
// 				try {
// 					const amount = tx.received?.amount || tx.expected?.amount || 0

// 					// Normalize dates for CSV export
// 					const orderDate = normalizeDate(
// 						tx.creationDateTime?.formated.date || tx.time.formated.date
// 					)
// 					const paymentDate = normalizeDate(
// 						tx.lastModifiedDateTime?.formated.date || tx.time.formated.date
// 					)

// 					let orderNum
// 					let orderId
// 					switch (tx.store?.store_type) {
// 						case "tiendanube":
// 							orderNum = tx.store.order_number
// 							orderId = tx.store.order_id
// 							break
// 						case "shopify":
// 							orderNum = tx.store.order_name ?? "-"
// 							orderId = tx.store.order_id ?? "-"
// 							break
// 						default:
// 							orderNum = "-"
// 							orderId = "-"
// 							break
// 					}

// 					const rowData = [
// 						escapeField(
// 							orderNum
// 						),
// 						escapeField(
// 							orderId
// 						),
// 						formatNumber(amount, tx.currency),
// 						escapeField(tx.currency ?? tx.credited?.currency),
// 						escapeField(tx.sender),
// 						escapeField(translateTransactionStatus(tx.status).toUpperCase()),
// 						escapeField(orderDate),
// 						escapeField(
// 							tx.creationDateTime?.time || tx.lastModifiedDateTime.time
// 						),
// 						escapeField(paymentDate),
// 						escapeField(tx.lastModifiedDateTime.time),
// 						escapeField(isOutbound ? "ENVÍO" : "COBRO"),
// 					].join(",")
// 					csvContent.push(rowData)
// 				} catch (error) {
// 					csvContent.push("-,-,-,-,-,-,-,-,-")
// 				}
// 			})
// 	}

// 	return csvContent
// }

// Helper function for downloading CSV
const formatDateForFileName = (date: Date): string => {
	const day = date.getDate().toString().padStart(2, "0")
	const month = (date.getMonth() + 1).toString().padStart(2, "0")
	const year = date.getFullYear()
	return `${day}-${month}-${year}`
}

const downloadXLS = (xlsContent: any[], suffix: string = "") => {
	try {
		const worksheet = utils.json_to_sheet(xlsContent)
		console.log("worksheet", worksheet)
		const workbook = utils.book_new()
		console.log("workbook", workbook)
		utils.book_append_sheet(workbook, worksheet, "Transacciones")
		console.log("workbook", workbook)
		const excelBuffer = write(workbook, {
			bookType: "xlsx",
			type: "array",
		})
		console.log("excelBuffer", excelBuffer)
		const blob = new Blob([excelBuffer], { type: "application/octet-stream" })
		saveAs(blob, `${suffix}.xlsx`)
	} catch (err) {
		console.log(err)
	}
}

const downloadCSV = (csvContent: string[], suffix: string = "") => {
	const BOM = "\uFEFF"
	const csvString = BOM + csvContent.join("\n")
	const blob = new Blob([csvString], { type: "text/csv;charset=utf-8" })
	const link = document.createElement("a")
	const url = URL.createObjectURL(blob)

	link.setAttribute("href", url)
	link.setAttribute("download", `Transacciones${suffix}.csv`)
	link.style.visibility = "hidden"

	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

// Modified export functions to use TableTransaction type
// export const exportAllPaymentsToCSV = (transactions: TableOrder[]) => {
// 	const csvContent = generateCSV(transactions)
// 	downloadCSV(csvContent)
// }

export const exportPaymentsInDateRange = async (
	data: TableOrder[] | TableMovement[],
	dateRange: DateRange,
	type: TableView = "orders"
) => {
	if (!dateRange.from) return

	const to = dateRange.to || dateRange.from
	const endDate = new Date(to.setHours(23, 59, 59, 999))

	const filteredData = data.filter((item) => {
		try {
			const txDate = item.time.time

			return (
				!isNaN(txDate.getTime()) &&
				txDate >= dateRange.from! &&
				txDate <= endDate
			)
		} catch (error) {
			return false
		}
	})

	const csvContent = generateXLS(
		filteredData as TableMovement[] | TableOrder[],
		type
	)

	const dateStr = dateRange.to
		? `${formatDateForFileName(dateRange.from)}_${formatDateForFileName(
				dateRange.to
		  )}`
		: formatDateForFileName(dateRange.from)

	const filePrefix = type === "orders" ? "Órdenes" : "Movimientos"
	downloadXLS(csvContent, `${filePrefix}_${dateStr}`)
}
